import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box2, Vector3 } from 'three';
import _ from 'underscore';

import { isDefined } from '../../../../../utils/variables';
import cameraManager from '../../../../../viewer/camera-manager';
import { SkyviewOrthographicCamera } from '../../../../../viewer/cameras/skyview-orthographic-camera';
import { SkyviewCamera } from '../../../../../viewer/cameras/types';
import { CoordinateManager } from '../../../../../viewer/coordinate-manager';
import { SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { WmsState, WmsToolLayerItem } from '../../../../../viewer/wms-state';
import { useSubscribe } from '../../../../hooks/use-subscribe';
import { SkyviewContext } from '../../../skyview/skyview-state';
import { updateWmsAttributions } from '../../../wms-map/update-wms-attributions';
import { MapLayers, WmsMap, WmsMapExtent } from '../../../wms-map/wms-map';

export const wmsCanvasRenderedEvent = 'wmsCanvasRendered';

function convertWmsExtentsToLocalExtents(wgs84Extents: Box2) {
  const projectProjection = CoordinateManager.getInstance().getProjectProjection();

  const projectMin = CoordinateManager.getInstance().convertCoords(
    wgs84Extents.min.x,
    wgs84Extents.min.y,
    'wms',
    projectProjection,
  );
  const projectMax = CoordinateManager.getInstance().convertCoords(
    wgs84Extents.max.x,
    wgs84Extents.max.y,
    'wms',
    projectProjection,
  );

  return new Box2(
    CoordinateManager.getInstance().projectToLocal(projectMin.x, projectMin.y),
    CoordinateManager.getInstance().projectToLocal(projectMax.x, projectMax.y),
  );
}

function calculateWmsExtentsFromLocalCoords(localCenter: Vector3, halfSize: number) {
  const projectProjection = CoordinateManager.getInstance().getProjectProjection();
  const center = CoordinateManager.getInstance().localToProjectCoords(localCenter);

  const min = CoordinateManager.getInstance().convertCoords(
    center.easting - halfSize,
    center.northing - halfSize,
    projectProjection,
    'wms',
  );
  const max = CoordinateManager.getInstance().convertCoords(
    center.easting + halfSize,
    center.northing + halfSize,
    projectProjection,
    'wms',
  );

  return { min, max };
}

const WmsSkyViewBridge = () => {
  const { skyview } = React.useContext(SkyviewContext);

  const [selectedWmsLayerItem, setSelectedWmsLayerItem] = useState<WmsToolLayerItem>(
    WmsState.instance.selectedLayerItem,
  );
  const wmsTargetCanvasRef = useRef<{ canvas: HTMLCanvasElement; extents: Box2 } | null>(null);
  const [cameraExtent, setCameraExtent] = useState<WmsMapExtent | null>(null);
  const [projection] = useState(WmsState.instance.viewerProjection);

  const handleCanvasSet = useCallback(
    (data: { canvas: HTMLCanvasElement; extents: Box2 } | null) => {
      wmsTargetCanvasRef.current = data;
      if (data) {
        skyview.distributeEvent({
          type: wmsCanvasRenderedEvent,
          data: {
            canvas: data.canvas,
            extents: convertWmsExtentsToLocalExtents(data.extents),
          },
        });
      } else {
        skyview.distributeEvent({ type: wmsCanvasRenderedEvent, data: undefined });
      }
    },
    [skyview],
  );

  /**
   * Note: Do not call directly, use "debouncedHandleCameraChange" in order to
   * debounce the event and not call it too many times.
   */
  const _handleCameraChange = useCallback(
    (camera?: SkyviewCamera) => {
      if (!skyview.isWmsMapAvailable()) {
        updateWmsAttributions(null);
        return;
      }

      if (camera instanceof SkyviewOrthographicCamera) {
        setCameraExtent(extractCameraExtent(camera));
      }
    },
    [skyview],
  );

  const debouncedHandleCameraChange = useMemo(
    () => _.debounce(_handleCameraChange, 100),
    [_handleCameraChange],
  );

  const handleLayerChange = useCallback(
    (layerItem: WmsToolLayerItem) => {
      if (!skyview.isWmsMapAvailable()) {
        return;
      }
      setSelectedWmsLayerItem(layerItem);
    },
    [skyview],
  );

  useSubscribe(SubscriptionTopic.OnCameraChanged, (message) => {
    debouncedHandleCameraChange(message.camera);
  });

  useSubscribe(SubscriptionTopic.OnCameraMoved, (message) => {
    debouncedHandleCameraChange(message.camera);
  });

  useSubscribe(SubscriptionTopic.OnSkyViewWmsMapToggled, () => {
    debouncedHandleCameraChange(cameraManager().getActiveCamera());
  });

  useSubscribe(SubscriptionTopic.OnSkyViewWmsLayerChanged, (message) => {
    handleLayerChange(message.layerItem);
  });

  useSubscribe(SubscriptionTopic.OnSceneChangeCompleted, () => {
    debouncedHandleCameraChange(cameraManager().getActiveCamera());
  });

  // Ensures that the canvas is repositioned when the origo is updated.
  useSubscribe(SubscriptionTopic.OnProjectionUpdated, () => {
    if (isDefined(wmsTargetCanvasRef.current)) {
      handleCanvasSet(wmsTargetCanvasRef.current);
    }
  });

  /**
   * Calculates the camera's viewing area in 'wms' projection coordinates.
   * It determines the largest 2D extent of the camera's current view and converts this area's corners to 'wms' projection.
   * @param camera The SkyviewOrthographicCamera instance.
   */
  const extractCameraExtent = (camera: SkyviewOrthographicCamera) => {
    const halfSize = Math.max(...camera.getViewSize().toArray()) / 2;
    const localCenter = new Vector3(0, 0, 0).unproject(camera);

    const { min, max } = calculateWmsExtentsFromLocalCoords(localCenter, halfSize);

    return {
      xmin: min.x,
      ymin: min.y,
      xmax: max.x,
      ymax: max.y,
    };
  };

  const transformWmsLayerItemToMapLayer = (layerItem: WmsToolLayerItem): MapLayers => {
    switch (layerItem) {
      case WmsToolLayerItem.Satellite:
        return MapLayers.Satellite;
      case WmsToolLayerItem.Road:
        return MapLayers.Road;
      default:
        throw new Error(`Unknown WMS layer item type: ${layerItem}`);
    }
  };

  return (
    <div>
      {isDefined(projection) && selectedWmsLayerItem !== WmsToolLayerItem.None && (
        <WmsMap
          mapExtent={cameraExtent}
          mapLayer={transformWmsLayerItemToMapLayer(selectedWmsLayerItem)}
          projection={projection}
          onCanvasSet={handleCanvasSet}
        />
      )}
    </div>
  );
};

export { WmsSkyViewBridge };
